/**
 * PreferenceFieldsEditor (Iter 9.40 - Pass A.2)
 *
 * Admin editor for `db.categories.{cat}.preference_fields`. Per ITER940 brief
 * § 5 every option carries an `image_url` (optional), and admins must be able
 * to set/replace them visually with no JSON.
 *
 * Each option is now a row: id (slug), label, image_url, enabled toggle.
 * Image upload uses POST /api/admin/cloudinary/sign + direct client upload,
 * but for now we accept a paste-in URL or click "Pick from media library"
 * (placeholder hook - `onPickImage` callback supplied by the page).
 *
 * Field-level extras still supported: required / help / type. Field type is
 * locked to `multi_select` per § 3 unless `allowAdvancedTypes` prop is set.
 */
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, X, ChevronUp, ChevronDown, GripVertical, Trash2, Image as ImageIcon, Check,
} from "lucide-react";

const FIELD_TYPES = [
  { value: "multi_select", label: "Multi-select (canonical)" },
  { value: "single_select", label: "Single select" },
  { value: "nested_multi_select", label: "Nested multi-select" },
  { value: "boolean", label: "Yes / No toggle" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "image_url", label: "Image URL" },
  { value: "text", label: "Text (notes only - exactly one per category)" },
];

const slug = (s) =>
  String(s || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");

export default function PreferenceFieldsEditor({
  value = [],
  onChange,
  testidPrefix = "pref",
  onPickImage,
}) {
  const fields = Array.isArray(value) ? value : [];

  const addField = () => {
    onChange([
      ...fields,
      {
        key: `field_${fields.length + 1}`,
        label: "New field",
        type: "multi_select",
        options: [],
        required: false,
        help: "",
      },
    ]);
  };

  const updateField = (idx, patch) => {
    onChange(fields.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  };

  const removeField = (idx) => onChange(fields.filter((_, i) => i !== idx));

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= fields.length) return;
    const next = [...fields];
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };

  return (
    <div className="space-y-3" data-testid={`${testidPrefix}-editor`}>
      {fields.length === 0 ? (
        <div className="border border-dashed rounded-lg p-6 text-center bg-gray-50">
          <p className="text-xs text-gray-500 mb-3">
            No fields yet. Clients won't see any preference questions.
          </p>
          <Button size="sm" onClick={addField} className="bg-black hover:bg-gray-800" data-testid={`${testidPrefix}-add-empty`}>
            <Plus className="w-3.5 h-3.5 mr-1" />Add first field
          </Button>
        </div>
      ) : (
        fields.map((field, idx) => (
          <FieldRow
            key={`${field.key || "field"}-${idx}`}
            field={field}
            idx={idx}
            total={fields.length}
            testidPrefix={testidPrefix}
            onUpdate={(p) => updateField(idx, p)}
            onRemove={() => removeField(idx)}
            onMove={(dir) => move(idx, dir)}
            onPickImage={onPickImage}
          />
        ))
      )}

      {fields.length > 0 && (
        <Button onClick={addField} variant="outline" size="sm" className="border-dashed" data-testid={`${testidPrefix}-add`}>
          <Plus className="w-3.5 h-3.5 mr-1" />Add field
        </Button>
      )}
    </div>
  );
}

function FieldRow({ field, idx, total, testidPrefix, onUpdate, onRemove, onMove, onPickImage }) {
  const [optDraftLabel, setOptDraftLabel] = useState("");
  const supportsOptions = field.type === "multi_select" ||
                          field.type === "single_select" ||
                          field.type === "nested_multi_select";

  // Normalize options to {id,label,image_url,enabled} (back-compat for str)
  const normOptions = (Array.isArray(field.options) ? field.options : []).map((o, i) => {
    if (typeof o === "string") return { id: slug(o), label: o, image_url: null, enabled: true };
    return {
      id: o.id || o.key || slug(o.label || `opt_${i}`),
      label: o.label || o.id || "",
      image_url: o.image_url || o.image || null,
      enabled: o.enabled !== false,
      ...o,
    };
  });
  const setNormOptions = (next) => onUpdate({ options: next });

  const addOption = () => {
    const v = optDraftLabel.trim();
    if (!v) return;
    const id = slug(v);
    if (normOptions.find((x) => x.id === id)) return;
    setNormOptions([...normOptions, { id, label: v, image_url: null, enabled: true }]);
    setOptDraftLabel("");
  };
  const removeOption = (id) => setNormOptions(normOptions.filter((x) => x.id !== id));
  const updateOption = (id, patch) =>
    setNormOptions(normOptions.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  const moveOption = (idx2, dir) => {
    const j = idx2 + dir;
    if (j < 0 || j >= normOptions.length) return;
    const next = [...normOptions];
    [next[idx2], next[j]] = [next[j], next[idx2]];
    setNormOptions(next);
  };

  return (
    <div className="border rounded-lg bg-white p-3 space-y-3" data-testid={`${testidPrefix}-row-${idx}`}>
      {/* Header */}
      <div className="flex items-center gap-2">
        <GripVertical className="w-4 h-4 text-gray-300" />
        <span className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">#{idx + 1}</span>
        <Badge variant="outline" className="text-[10px]" data-testid={`${testidPrefix}-row-type-${idx}`}>
          {FIELD_TYPES.find((t) => t.value === field.type)?.label || field.type}
        </Badge>
        <div className="flex-1" />
        <button type="button" onClick={() => onMove(-1)} disabled={idx === 0}
          className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
          data-testid={`${testidPrefix}-row-up-${idx}`}>
          <ChevronUp className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => onMove(1)} disabled={idx === total - 1}
          className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
          data-testid={`${testidPrefix}-row-down-${idx}`}>
          <ChevronDown className="w-4 h-4" />
        </button>
        <button type="button" onClick={onRemove}
          className="text-gray-400 hover:text-red-500"
          data-testid={`${testidPrefix}-row-remove-${idx}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Label / key / type */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <div>
          <Label className="text-[11px] text-gray-500">Label</Label>
          <Input
            value={field.label || ""}
            onChange={(e) => {
              const newLabel = e.target.value;
              const patch = { label: newLabel };
              const prevAutoKey = slug(field.label || "");
              if (!field.key || field.key === prevAutoKey) {
                patch.key = slug(newLabel);
              }
              onUpdate(patch);
            }}
            data-testid={`${testidPrefix}-row-label-${idx}`}
          />
        </div>
        <div>
          <Label className="text-[11px] text-gray-500">Field key</Label>
          <Input
            value={field.key || ""}
            onChange={(e) => onUpdate({ key: slug(e.target.value) })}
            placeholder={slug(field.label) || "field_key"}
            data-testid={`${testidPrefix}-row-key-${idx}`}
          />
        </div>
        <div>
          <Label className="text-[11px] text-gray-500">Type</Label>
          <Select value={field.type || "multi_select"} onValueChange={(v) => onUpdate({ type: v })}>
            <SelectTrigger className="text-sm" data-testid={`${testidPrefix}-row-type-trigger-${idx}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FIELD_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value} data-testid={`${testidPrefix}-row-type-opt-${t.value}-${idx}`}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Options editor */}
      {supportsOptions && (
        <div className="space-y-2">
          <Label className="text-[11px] text-gray-500">Options</Label>
          <div className="space-y-1.5">
            {normOptions.map((opt, i2) => (
              <div
                key={opt.id}
                className="flex items-center gap-2 border rounded p-1.5 bg-gray-50"
                data-testid={`${testidPrefix}-row-opt-${idx}-${opt.id}`}
              >
                {opt.image_url ? (
                  <img src={opt.image_url} alt="" className="w-8 h-8 rounded object-cover" />
                ) : (
                  <div className="w-8 h-8 rounded bg-gray-200 flex items-center justify-center text-gray-400">
                    <ImageIcon className="w-3 h-3" />
                  </div>
                )}
                <Input
                  value={opt.label}
                  onChange={(e) => updateOption(opt.id, { label: e.target.value })}
                  placeholder="Label"
                  className="h-7 text-xs flex-1"
                  data-testid={`${testidPrefix}-row-opt-label-${idx}-${i2}`}
                />
                <Input
                  value={opt.image_url || ""}
                  onChange={(e) => updateOption(opt.id, { image_url: e.target.value || null })}
                  placeholder="Image URL"
                  className="h-7 text-xs w-44"
                  data-testid={`${testidPrefix}-row-opt-image-${idx}-${i2}`}
                />
                {onPickImage && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 px-2"
                    onClick={async () => {
                      const url = await onPickImage(opt);
                      if (url) updateOption(opt.id, { image_url: url });
                    }}
                    data-testid={`${testidPrefix}-row-opt-pick-${idx}-${i2}`}
                  >
                    <ImageIcon className="w-3 h-3" />
                  </Button>
                )}
                <button
                  type="button"
                  onClick={() => updateOption(opt.id, { enabled: !opt.enabled })}
                  className={`text-[10px] px-1.5 py-0.5 rounded border ${
                    opt.enabled ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-gray-100 text-gray-500 border-gray-200"
                  }`}
                  data-testid={`${testidPrefix}-row-opt-enabled-${idx}-${i2}`}
                >
                  {opt.enabled ? <Check className="w-3 h-3 inline" /> : "off"}
                </button>
                <button
                  type="button"
                  onClick={() => moveOption(i2, -1)}
                  disabled={i2 === 0}
                  className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
                >
                  <ChevronUp className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => moveOption(i2, 1)}
                  disabled={i2 === normOptions.length - 1}
                  className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
                >
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => removeOption(opt.id)}
                  className="text-gray-400 hover:text-red-500"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            <div className="flex items-center gap-1.5">
              <Input
                value={optDraftLabel}
                onChange={(e) => setOptDraftLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); addOption(); }
                }}
                placeholder="Type label & press Enter"
                className="h-7 text-xs flex-1"
                data-testid={`${testidPrefix}-row-opt-input-${idx}`}
              />
              <Button size="sm" variant="outline" onClick={addOption}
                className="h-7 px-2" data-testid={`${testidPrefix}-row-opt-add-${idx}`}>
                <Plus className="w-3 h-3" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Helper / required */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 items-end">
        <div>
          <Label className="text-[11px] text-gray-500">Helper text (optional)</Label>
          <Input
            value={field.help || ""}
            onChange={(e) => onUpdate({ help: e.target.value })}
            placeholder="Small grey text shown under the field"
            data-testid={`${testidPrefix}-row-help-${idx}`}
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-gray-600 pb-1.5">
          <input type="checkbox" checked={!!field.required}
            onChange={(e) => onUpdate({ required: e.target.checked })}
            data-testid={`${testidPrefix}-row-required-${idx}`}
          />
          Required to submit
        </label>
      </div>
    </div>
  );
}
